import React, { useEffect, useState, useCallback } from "react";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Mail, Download, RefreshCw, Users, TrendingUp, CheckCircle2, XCircle, Clock,
} from "lucide-react";

export default function Prospects() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
      setError("");
    } catch (e) {
      setError(e?.response?.status === 403
        ? "Accès réservé à l'administrateur"
        : "Erreur de chargement");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const headers = ["email", "full_name", "score", "monthly_revenue", "after_charges", "regime", "captured_at", "next_email_step", "converted", "unsubscribed"];
    const rows = leads.map((l) =>
      headers.map((h) => {
        const v = l[h] ?? "";
        return `"${String(v).replace(/"/g, '""')}"`;
      }).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `prospects-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = {
    total: leads.length,
    converted: leads.filter((l) => l.converted).length,
    inSequence: leads.filter((l) => !l.converted && !l.unsubscribed && l.next_email_step !== "done").length,
    unsubscribed: leads.filter((l) => l.unsubscribed).length,
  };

  if (error) return <div className="card-soft p-8 text-center text-clay" data-testid="prospects-error">{error}</div>;

  return (
    <div className="space-y-6" data-testid="prospects-container">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Prospects</h1>
          <p className="text-muted-soft mt-1">
            Visiteurs qui ont laissé leur email après le diagnostic.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-ghost text-sm" data-testid="prospects-refresh">
            <RefreshCw className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} />
            Actualiser
          </button>
          <button onClick={exportCsv} disabled={leads.length === 0} className="btn-sage text-sm" data-testid="prospects-export">
            <Download className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} />
            Exporter CSV
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total prospects" value={stats.total} color="var(--sage)" testid="stat-total" />
        <StatCard icon={TrendingUp} label="Convertis" value={stats.converted} color="#7B8C7A" testid="stat-converted" />
        <StatCard icon={Clock} label="En séquence" value={stats.inSequence} color="#B58B5C" testid="stat-sequence" />
        <StatCard icon={XCircle} label="Désinscrits" value={stats.unsubscribed} color="#A04E3A" testid="stat-unsub" />
      </div>

      {loading ? (
        <div className="card-soft p-12 text-center text-muted-soft">Chargement…</div>
      ) : leads.length === 0 ? (
        <div className="card-soft p-12 text-center" data-testid="prospects-empty">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-sage-light flex items-center justify-center mb-4">
            <Mail className="w-7 h-7 text-sage" strokeWidth={1.4} />
          </div>
          <div className="font-display text-xl mb-2">Aucun prospect pour l'instant</div>
          <p className="text-muted-soft max-w-md mx-auto">
            Dès qu'un visiteur de votre landing page laisse son email après le diagnostic, il apparaît ici.
          </p>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="sm:hidden space-y-3" data-testid="prospects-cards">
            {leads.map((l) => <LeadCard key={l.id} l={l} />)}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block card-soft overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-soft" style={{ backgroundColor: "var(--surface)" }}>
                  <th className="text-left px-4 py-3 font-medium">Email</th>
                  <th className="text-left px-4 py-3 font-medium">Prénom</th>
                  <th className="text-left px-4 py-3 font-medium">CA / Net</th>
                  <th className="text-left px-4 py-3 font-medium">Score</th>
                  <th className="text-left px-4 py-3 font-medium">Date</th>
                  <th className="text-left px-4 py-3 font-medium">Étape</th>
                  <th className="text-left px-4 py-3 font-medium">État</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-4 font-medium break-all">{l.email}</td>
                    <td className="px-4 py-4">{l.full_name || "—"}</td>
                    <td className="px-4 py-4 text-xs">
                      {Math.round(l.monthly_revenue)} € → {Math.round(l.after_charges)} €
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-xs font-medium" style={{ color: l.score >= 60 ? "var(--sage)" : "#A04E3A" }}>
                        {l.score}/100
                      </span>
                    </td>
                    <td className="px-4 py-4 text-xs text-muted-soft">
                      {(l.captured_at || "").slice(0, 10)}
                    </td>
                    <td className="px-4 py-4">
                      <StepBadge step={l.next_email_step} />
                    </td>
                    <td className="px-4 py-4">
                      <StateBadge l={l} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="card-soft p-5" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-sage-light flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.5} />
        </div>
        <div>
          <div className="text-xs text-muted-soft">{label}</div>
          <div className="font-display text-2xl">{value}</div>
        </div>
      </div>
    </div>
  );
}

function StepBadge({ step }) {
  const labels = {
    leviers: { text: "J+2 — 3 leviers", color: "#8B6F4D", bg: "#F5E8D8" },
    marie: { text: "J+5 — Marie", color: "#8B6F4D", bg: "#F5E8D8" },
    last_call: { text: "J+10 — Last call", color: "#8B4D3D", bg: "#F5E5DC" },
    done: { text: "Séquence terminée", color: "var(--text-muted)", bg: "var(--sage-light)" },
  };
  const s = labels[step] || labels.done;
  return <span className="text-xs px-2.5 py-1 rounded-full" style={{ color: s.color, backgroundColor: s.bg }}>{s.text}</span>;
}

function StateBadge({ l }) {
  if (l.converted) return <span className="text-xs px-2.5 py-1 rounded-full text-sage" style={{ backgroundColor: "var(--sage-light)" }}><CheckCircle2 className="inline w-3 h-3 mr-1" />Converti</span>;
  if (l.unsubscribed) return <span className="text-xs px-2.5 py-1 rounded-full" style={{ color: "#A04E3A", backgroundColor: "#F5E5DC" }}><XCircle className="inline w-3 h-3 mr-1" />Désinscrit</span>;
  return <span className="text-xs px-2.5 py-1 rounded-full text-muted-soft" style={{ backgroundColor: "var(--surface)" }}><Clock className="inline w-3 h-3 mr-1" />Actif</span>;
}

function LeadCard({ l }) {
  return (
    <div className="card-soft p-4" data-testid={`lead-card-${l.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm break-all">{l.email}</div>
          {l.full_name && <div className="text-xs text-muted-soft">{l.full_name}</div>}
        </div>
        <div className="text-sm font-medium" style={{ color: l.score >= 60 ? "var(--sage)" : "#A04E3A" }}>
          {l.score}/100
        </div>
      </div>
      <div className="text-xs text-muted-soft mb-2">
        CA {Math.round(l.monthly_revenue)} € → Net {Math.round(l.after_charges)} € · {(l.captured_at || "").slice(0, 10)}
      </div>
      <div className="flex flex-wrap gap-1">
        <StepBadge step={l.next_email_step} />
        <StateBadge l={l} />
      </div>
    </div>
  );
}
